package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.HelpOutline
import androidx.compose.material.icons.outlined.History
import androidx.compose.material.icons.outlined.Send
import androidx.compose.material.icons.outlined.SmartToy
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AsistenteConversacion
import org.gymly.model.AsistenteEstado
import org.gymly.model.AsistentePersonalizacion
import org.gymly.model.AsistentePreguntaSinRespuesta
import org.gymly.model.AsistenteRespuesta
import org.gymly.theme.AdminColors
import org.gymly.util.userMessage

private data class MensajeAsistente(val delUsuario: Boolean, val texto: String)

/** Pregunta rápida: lo que ve el usuario y la clave que el servidor consulta directamente, sin modelo. */
private data class PreguntaRapida(val texto: String, val clave: String, val descripcion: String = "")

/**
 * Temas del catálogo de ayuda. Los botones dicen qué responden; los ejemplos son preguntas que se escriben
 * en el chat (el modelo las contesta con las herramientas). Todo esto es fijo: no necesita modelo.
 */
private data class GrupoPreguntas(
    val titulo: String,
    val preguntas: List<PreguntaRapida>,
    val ejemplos: List<String>,
)

private val GRUPOS_PREGUNTAS = listOf(
    GrupoPreguntas(
        "Cuánto se vende",
        listOf(
            PreguntaRapida(
                "Ventas de tienda esta semana", "ventas_semana",
                "Cantidad de ventas y total en Bs y en $ de los últimos 7 días.",
            ),
        ),
        ejemplos = listOf(
            "¿Cuánto vendimos de proteína esta semana?",
            "¿Cuánto vendimos ayer?",
        ),
    ),
    GrupoPreguntas(
        "Membresías cobradas",
        listOf(
            PreguntaRapida(
                "Membresías pagadas esta semana", "membresias_semana",
                "Cuántas membresías se pagaron en los últimos 7 días y su total en Bs.",
            ),
        ),
        ejemplos = listOf(
            "¿Cuántas membresías se pagaron hoy?",
        ),
    ),
    GrupoPreguntas(
        "Socios y asistencia",
        listOf(
            PreguntaRapida(
                "Socios sin asistir en el último mes", "sin_asistencia_mes",
                "Cuántos socios activos no han ido en 30 días. Muestra cifras, no nombres.",
            ),
        ),
        ejemplos = listOf(
            "¿Cuántos socios activos hay?",
        ),
    ),
    GrupoPreguntas(
        "Productos y stock",
        listOf(
            PreguntaRapida(
                "Productos bajo el mínimo", "stock_bajo",
                "Productos con stock igual o menor a su mínimo, con cuánto hay de cada uno.",
            ),
        ),
        ejemplos = listOf(
            "¿Cuánto stock tengo de agua?",
            "¿Cuántas unidades de proteína se vendieron este mes?",
        ),
    ),
)

/** Todas las preguntas en una lista, para el chat (donde no caben los temas). */
private val PREGUNTAS_RAPIDAS = GRUPOS_PREGUNTAS.flatMap { it.preguntas }

private const val COLOR_POR_DEFECTO = "#1E5BD8"

/**
 * Estado compartido por el botón flotante y la pantalla de Agentes: el interruptor y la personalización
 * (nombre, color, logo) se actualizan sin recargar la app.
 */
object EstadoAsistenteCompartido {
    var estado by mutableStateOf<AsistenteEstado?>(null)
    var personalizacion by mutableStateOf<AsistentePersonalizacion?>(null)
}

/** Botón flotante del asistente. Solo aparece si está encendido y el usuario tiene el permiso. */
@Composable
fun AsistenteFlotante(modifier: Modifier = Modifier) {
    var abierto by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        EstadoAsistenteCompartido.estado = runCatching { AdminRepo.api.estadoAsistente() }.getOrNull()
        EstadoAsistenteCompartido.personalizacion =
            runCatching { AdminRepo.api.obtenerPersonalizacionAsistente() }.getOrNull()
    }
    val actual = EstadoAsistenteCompartido.estado
    // Apagado en Configuración o sin permiso: no hay botón, así no queda ninguna función visible.
    if (actual == null || !actual.habilitado || !actual.puedeUsar) return

    val marca = marcaActual()
    Box(modifier.padding(16.dp), contentAlignment = Alignment.BottomEnd) {
        if (abierto) {
            PanelAsistente(chatLibre = actual.chatLibre, marca = marca, onCerrar = { abierto = false })
        } else {
            Row(
                Modifier
                    .clip(RoundedCornerShape(28.dp))
                    .background(colorDeHex(marca.color))
                    .clickable { abierto = true }
                    .padding(start = 8.dp, end = 18.dp, top = 8.dp, bottom = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                MarcaAsistente(logoUrl = marca.logoUrl, nombre = marca.nombre, tamano = 30.dp)
                Spacer(Modifier.width(10.dp))
                Text(marca.nombre, color = Color.White, fontWeight = FontWeight.SemiBold, fontSize = 15.sp)
            }
        }
    }
}

/** Personalización vigente, o la de por defecto mientras carga. */
fun marcaActual(): AsistentePersonalizacion =
    EstadoAsistenteCompartido.personalizacion ?: AsistentePersonalizacion("Asistente", COLOR_POR_DEFECTO, null)

/** Logo de la marca si hay URL; si no, un icono sobre un círculo translúcido. */
@Composable
fun MarcaAsistente(logoUrl: String?, nombre: String, tamano: Dp) {
    Box(
        Modifier
            .size(tamano)
            .clip(CircleShape)
            .background(Color.White.copy(alpha = 0.2f)),
        contentAlignment = Alignment.Center,
    ) {
        if (logoUrl.isNullOrBlank()) {
            Icon(Icons.Outlined.SmartToy, contentDescription = nombre, tint = Color.White, modifier = Modifier.size(tamano * 0.6f))
        } else {
            AsyncImage(
                model = logoUrl,
                contentDescription = nombre,
                modifier = Modifier.size(tamano).clip(CircleShape),
            )
        }
    }
}

/** Convierte "#RRGGBB" en un color. Un valor no válido cae al azul de la marca. */
fun colorDeHex(hex: String): Color {
    val valor = hex.removePrefix("#").toLongOrNull(16) ?: return Color(0xFF1E5BD8)
    return Color(0xFF000000L or valor)
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun PanelAsistente(chatLibre: Boolean, marca: AsistentePersonalizacion, onCerrar: () -> Unit) {
    val scope = rememberCoroutineScope()
    val listaMensajes = rememberLazyListState()
    var mensajes by remember { mutableStateOf(listOf<MensajeAsistente>()) }
    var conversacionId by remember { mutableStateOf<String?>(null) }
    var pregunta by remember { mutableStateOf("") }
    var pensando by remember { mutableStateOf(false) }
    var verSinRespuesta by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var verHistorial by remember { mutableStateOf(false) }
    var conversaciones by remember { mutableStateOf<List<AsistenteConversacion>>(emptyList()) }
    val color = colorDeHex(marca.color)

    /** Muestra la pregunta, espera la respuesta y guarda la conversación para las siguientes. */
    fun responder(textoUsuario: String, pedir: suspend (String?) -> AsistenteRespuesta) {
        if (pensando) return
        mensajes = mensajes + MensajeAsistente(delUsuario = true, texto = textoUsuario)
        pensando = true
        error = null
        scope.launch {
            try {
                val respuesta = pedir(conversacionId)
                conversacionId = respuesta.conversacionId ?: conversacionId
                mensajes = mensajes + MensajeAsistente(delUsuario = false, texto = respuesta.texto)
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo consultar el asistente.")
            } finally {
                pensando = false
            }
        }
    }

    fun cargarHistorial() {
        scope.launch {
            conversaciones = runCatching { AdminRepo.api.listarConversacionesAsistente() }.getOrDefault(emptyList())
        }
    }

    /** Abre una conversación propia: sus mensajes reemplazan el chat y las siguientes preguntas continúan ahí. */
    fun abrirConversacion(id: String) {
        scope.launch {
            try {
                val lista = AdminRepo.api.leerConversacionAsistente(id)
                mensajes = lista.map { MensajeAsistente(delUsuario = it.rol == "USUARIO", texto = it.texto) }
                conversacionId = id
                verHistorial = false
                error = null
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo abrir la conversación.")
            }
        }
    }

    fun enviarLibre() {
        val limpio = pregunta.trim()
        if (limpio.isBlank() || pensando || !chatLibre) return
        pregunta = ""
        responder(limpio) { id -> AdminRepo.api.preguntarAsistente(limpio, "PASOS", id) }
    }

    fun enviarRapida(rapida: PreguntaRapida) {
        responder(rapida.texto) { id -> AdminRepo.api.preguntarRapidaAsistente(rapida.clave, id) }
    }

    LaunchedEffect(mensajes.size) {
        if (mensajes.isNotEmpty()) listaMensajes.animateScrollToItem(mensajes.size - 1)
    }

    Column(
        Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            .heightIn(max = 680.dp)
            .shadow(12.dp, RoundedCornerShape(24.dp))
            .clip(RoundedCornerShape(24.dp))
            .background(AdminColors.Surface)
            .background(color.copy(alpha = 0.05f))
            .border(1.dp, color.copy(alpha = 0.45f), RoundedCornerShape(24.dp)),
    ) {
        // Encabezado en el color de la marca: identidad, estado y acciones con nombre.
        Row(
            Modifier.fillMaxWidth().background(color).padding(start = 14.dp, end = 6.dp, top = 10.dp, bottom = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MarcaAsistente(logoUrl = marca.logoUrl, nombre = marca.nombre, tamano = 36.dp)
            Column(Modifier.weight(1f).padding(start = 10.dp)) {
                Text(
                    marca.nombre,
                    color = Color.White,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 16.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    if (chatLibre) "En línea" else "Solo consultas rápidas",
                    color = Color.White.copy(alpha = 0.8f),
                    fontSize = 12.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            IconButton(modifier = Modifier.size(38.dp), onClick = {
                verHistorial = !verHistorial
                if (verHistorial) cargarHistorial()
            }) {
                Icon(Icons.Outlined.History, contentDescription = if (verHistorial) "Volver al chat" else "Historial", tint = Color.White)
            }
            IconButton(modifier = Modifier.size(38.dp), onClick = {
                mensajes = emptyList()
                conversacionId = null
                error = null
                verHistorial = false
            }, enabled = !pensando) {
                Icon(Icons.Outlined.Add, contentDescription = "Nueva conversación", tint = Color.White)
            }
            IconButton(modifier = Modifier.size(38.dp), onClick = { verSinRespuesta = true }) {
                Icon(Icons.Outlined.HelpOutline, contentDescription = "Preguntas sin respuesta", tint = Color.White)
            }
            if (verSinRespuesta) {
                PreguntasSinRespuestaDialog(onCerrar = { verSinRespuesta = false })
            }
            IconButton(modifier = Modifier.size(38.dp), onClick = onCerrar) {
                Icon(Icons.Outlined.Close, contentDescription = "Cerrar asistente", tint = Color.White)
            }
        }

        if (verHistorial) {
            HistorialConversaciones(conversaciones, Modifier.weight(1f).padding(12.dp), onAbrir = { abrirConversacion(it) })
        } else {
            if (mensajes.isEmpty()) {
                BienvenidaAsistente(marca.nombre, chatLibre, onRapida = { enviarRapida(it) }, deshabilitado = pensando)
            } else {
                LazyColumn(
                    state = listaMensajes,
                    modifier = Modifier.weight(1f).fillMaxWidth().padding(horizontal = 12.dp),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    items(mensajes) { mensaje -> Burbuja(mensaje, color, marca) }
                    if (pensando) {
                        item { Indicador(color) }
                    }
                }
            }

            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(horizontal = 16.dp, vertical = 4.dp))
            }

            if (mensajes.isNotEmpty()) {
                FlowRow(
                    Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 6.dp),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    PREGUNTAS_RAPIDAS.forEach { rapida ->
                        OutlinedButton(
                            onClick = { enviarRapida(rapida) },
                            enabled = !pensando,
                            shape = RoundedCornerShape(16.dp),
                        ) { Text(rapida.texto, fontSize = 12.sp) }
                    }
                }
            }

            // Barra de envío: campo redondeado con el botón de enviar al lado.
            Row(
                Modifier.fillMaxWidth().padding(start = 12.dp, end = 12.dp, top = 6.dp, bottom = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                OutlinedTextField(
                    value = pregunta,
                    onValueChange = { pregunta = it },
                    enabled = !pensando && chatLibre,
                    maxLines = 3,
                    shape = RoundedCornerShape(20.dp),
                    placeholder = {
                        Text(if (chatLibre) "Escribe tu pregunta" else "El chat libre no está configurado")
                    },
                    modifier = Modifier.weight(1f),
                )
                IconButton(
                    onClick = { enviarLibre() },
                    enabled = !pensando && chatLibre && pregunta.isNotBlank(),
                ) {
                    Icon(Icons.Outlined.Send, contentDescription = "Enviar", tint = color)
                }
            }
        }
    }
}

/** Primera pantalla del chat: saludo y las preguntas rápidas como opciones claras. */
@Composable
private fun BienvenidaAsistente(
    nombre: String,
    chatLibre: Boolean,
    onRapida: (PreguntaRapida) -> Unit,
    deshabilitado: Boolean,
) {
    var mostrarAyuda by remember { mutableStateOf(false) }
    Column(
        Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Text("Hola, soy $nombre", fontWeight = FontWeight.SemiBold, fontSize = 18.sp, color = AdminColors.Text)
        Text(
            if (chatLibre) {
                "Pregúntame por ventas, membresías, asistencia o stock. Las cifras salen de la base del gimnasio."
            } else {
                "Empieza con una consulta rápida. El chat libre todavía no está configurado."
            },
            color = AdminColors.TextMuted,
            fontSize = 13.sp,
        )
        Spacer(Modifier.height(6.dp))
        TextButton(onClick = { mostrarAyuda = true }) {
            Text("¿Qué puedo preguntar?", color = AdminColors.Primary, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
        }
        GruposPreguntas(enabled = !deshabilitado, onRapida = onRapida)
    }
    if (mostrarAyuda) {
        AyudaPreguntas(onCerrar = { mostrarAyuda = false })
    }
}

/**
 * Lista de preguntas que el asistente no pudo responder con datos. La ve quien puede revisar
 * Configuración de seguridad; a los demás el servidor les niega la lista y se muestra el aviso.
 */
@Composable
private fun PreguntasSinRespuestaDialog(onCerrar: () -> Unit) {
    var lista by remember { mutableStateOf<List<AsistentePreguntaSinRespuesta>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    LaunchedEffect(Unit) {
        runCatching { AdminRepo.api.preguntasSinRespuestaAsistente() }
            .onSuccess { lista = it }
            .onFailure {
                error = "No puedes revisar estas preguntas. Lo ve quien tiene permiso de Configuración de seguridad."
            }
    }
    AlertDialog(
        onDismissRequest = onCerrar,
        confirmButton = {
            TextButton(onClick = onCerrar) { Text("Cerrar", color = AdminColors.Primary) }
        },
        title = { Text("Preguntas sin respuesta", fontWeight = FontWeight.SemiBold) },
        text = {
            Column(
                Modifier.verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text(
                    "Preguntas que el asistente no pudo responder con datos. Si se repiten, conviene agregar una herramienta para ellas.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
                val actual = lista
                when {
                    error != null -> Unit
                    actual == null -> Text("Cargando…", color = AdminColors.TextMuted, fontSize = 13.sp)
                    actual.isEmpty() -> Text("No hay preguntas sin respuesta.", color = AdminColors.TextMuted, fontSize = 13.sp)
                    else -> actual.forEach { p ->
                        Column {
                            Text(p.pregunta, color = AdminColors.Text, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                            Text(
                                "${p.veces} ${if (p.veces == 1) "vez" else "veces"} · última: ${p.ultima.take(16).replace('T', ' ')}",
                                color = AdminColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                    }
                }
            }
        },
    )
}

/** Catálogo de ayuda: temas y ejemplos de lo que se puede preguntar. No consulta datos ni usa el modelo. */
@Composable
private fun AyudaPreguntas(onCerrar: () -> Unit) {
    AlertDialog(
        onDismissRequest = onCerrar,
        confirmButton = {
            TextButton(onClick = onCerrar) { Text("Entendido", color = AdminColors.Primary) }
        },
        title = { Text("Qué puedo preguntar", fontWeight = FontWeight.SemiBold) },
        text = {
            Column(
                Modifier.verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(14.dp),
            ) {
                Text(
                    "Los botones responden al instante con las cifras del gimnasio. Para otra pregunta, escríbela en el chat.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                GRUPOS_PREGUNTAS.forEach { grupo ->
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            grupo.titulo.uppercase(),
                            color = AdminColors.Primary,
                            fontWeight = FontWeight.Bold,
                            fontSize = 12.sp,
                            letterSpacing = 0.8.sp,
                        )
                        grupo.ejemplos.forEach { ejemplo ->
                            Text("• $ejemplo", color = AdminColors.Text, fontSize = 13.sp)
                        }
                    }
                }
            }
        },
    )
}

/** Cada tema con su encabezado; debajo, cada respuesta rápida con una línea que dice qué recibirás. */
@Composable
private fun GruposPreguntas(enabled: Boolean, onRapida: (PreguntaRapida) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        GRUPOS_PREGUNTAS.forEach { grupo ->
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text(
                    grupo.titulo.uppercase(),
                    color = AdminColors.Primary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 12.sp,
                    letterSpacing = 0.8.sp,
                )
                grupo.preguntas.forEach { rapida ->
                    OutlinedButton(
                        onClick = { onRapida(rapida) },
                        enabled = enabled,
                        shape = RoundedCornerShape(16.dp),
                        modifier = Modifier.fillMaxWidth().heightIn(min = 44.dp),
                    ) { Text(rapida.texto, fontSize = 13.sp) }
                    if (rapida.descripcion.isNotBlank()) {
                        Text(rapida.descripcion, color = AdminColors.TextMuted, fontSize = 12.sp)
                    }
                }
            }
        }
    }
}

@Composable
private fun Burbuja(mensaje: MensajeAsistente, color: Color, marca: AsistentePersonalizacion) {
    if (mensaje.delUsuario) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            Text(
                mensaje.texto,
                color = Color.White,
                fontSize = 14.sp,
                modifier = Modifier
                    .widthIn(max = 420.dp)
                    .clip(RoundedCornerShape(18.dp, 18.dp, 4.dp, 18.dp))
                    .background(color)
                    .padding(horizontal = 14.dp, vertical = 10.dp),
            )
        }
    } else {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top) {
            MarcaAsistente(logoUrl = marca.logoUrl, nombre = marca.nombre, tamano = 28.dp)
            Spacer(Modifier.width(8.dp))
            Text(
                mensaje.texto,
                color = AdminColors.Text,
                fontSize = 14.sp,
                modifier = Modifier
                    .widthIn(max = 420.dp)
                    .clip(RoundedCornerShape(18.dp, 18.dp, 18.dp, 4.dp))
                    .background(AdminColors.Surface)
                    .border(1.dp, color.copy(alpha = 0.35f), RoundedCornerShape(18.dp, 18.dp, 18.dp, 4.dp))
                    .padding(horizontal = 14.dp, vertical = 10.dp),
            )
        }
    }
}

@Composable
private fun Indicador(color: Color) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        CircularProgressIndicator(Modifier.size(16.dp), color = color, strokeWidth = 2.dp)
        Spacer(Modifier.width(8.dp))
        Text("Consultando…", fontSize = 12.sp, color = AdminColors.TextMuted)
    }
}

/** Lista de conversaciones propias. Tocar una la abre con sus mensajes. */
@Composable
private fun HistorialConversaciones(
    conversaciones: List<AsistenteConversacion>,
    modifier: Modifier,
    onAbrir: (String) -> Unit,
) {
    if (conversaciones.isEmpty()) {
        Text("Todavía no tienes conversaciones.", color = AdminColors.TextMuted, fontSize = 13.sp, modifier = modifier)
        return
    }
    Column(
        modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        conversaciones.forEach { conversacion ->
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(14.dp))
                    .background(AdminColors.SurfaceContainer)
                    .clickable { onAbrir(conversacion.id) }
                    .padding(12.dp),
            ) {
                Text(conversacion.primeraPregunta, fontSize = 14.sp, color = AdminColors.Text, maxLines = 2)
                Text(conversacion.creadaEn.replace('T', ' ').take(16), fontSize = 11.sp, color = AdminColors.TextMuted)
            }
        }
    }
}
